import { useCallback } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { LoadingButton } from "@mui/lab";
import { Tooltip } from "@mui/material";
import Download from "@mui/icons-material/Download";

import { DateTime } from "luxon";

import { useAppSelector } from "app/hooks";
import { selectStreamingApiBaseUrl } from "features/config/store/selectors";
import { useRetrieveRelativeDownloadUrlMutation } from "services/streamingApi/recordings.api";

export interface DownloadRecordingButtonProps {
    recordingId: EntityId;
    deviceId: EntityId;
    startTime: string;
    stopTime: string;
}

export function DownloadRecordingButton(props: DownloadRecordingButtonProps) {
    const streamingApiBaseUrl = useAppSelector((state) =>
        selectStreamingApiBaseUrl(state),
    );

    const [retrieveRelativeDownloadUrl, { isLoading }] =
        useRetrieveRelativeDownloadUrlMutation();

    const onClick = useCallback(async () => {
        const cleanStartTime = DateTime.fromISO(
            props.startTime,
        ).toISO() as string;
        const cleanStopTime = DateTime.fromISO(
            props.stopTime,
        ).toISO() as string;

        const relativeDownloadUrl = await retrieveRelativeDownloadUrl({
            deviceId: props.deviceId,
            recordingId: props.recordingId,
            startTime: cleanStartTime,
            stopTime: cleanStopTime,
        }).unwrap();

        const absoluteDownloadUrl = `${streamingApiBaseUrl}${relativeDownloadUrl}`;

        const hiddenElement = document.createElement("a");
        hiddenElement.href = absoluteDownloadUrl;
        hiddenElement.target = "_blank";
        hiddenElement.click();
    }, [
        props.startTime,
        props.stopTime,
        props.deviceId,
        props.recordingId,
        retrieveRelativeDownloadUrl,
        streamingApiBaseUrl,
    ]);

    return (
        <Tooltip title="Download">
            <LoadingButton
                variant="text"
                loadingPosition="center"
                loading={isLoading}
                onClick={onClick}
            >
                <Download />
            </LoadingButton>
        </Tooltip>
    );
}
